import React, { useEffect, useState } from "react"
import { Dialog, Box, Typography, Button } from "@mui/material"
import { styled } from "@mui/material/styles"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  fetchSubscriptionStatus,
  selectMigration,
  selectPending,
  selectTrial,
} from "../app/features/subscriptionStatusSlice"
import {
  PENDING_UI_HIDDEN_PATHS,
  markSeen,
  migrationNotice,
  openPendingPayment,
  pendingActionLabel,
  pendingNotice,
  wasSeen,
} from "./pendingPayment"

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    margin: "16px",
    maxWidth: "440px",
    width: "calc(100% - 32px)",
    backgroundColor: "#F2E1D1",
  },
})

const font = "'InterTight', sans-serif"

const primaryButtonSx = {
  py: 1.4,
  borderRadius: "6px",
  textTransform: "none",
  fontFamily: font,
  fontWeight: 600,
  backgroundColor: "#44795A",
  "&:hover": { backgroundColor: "#365E48" },
}

const outlinedButtonSx = {
  py: 1.4,
  borderRadius: "6px",
  textTransform: "none",
  fontFamily: font,
  fontWeight: 600,
  color: "#44795A",
  borderColor: "#44795A",
}

const textButtonSx = { textTransform: "none", fontFamily: font, color: "#6B6B55" }

// Keys hold only the payment ref / the deadline — never URLs or QR payloads.
// A Pix trial's charge keeps its ref when the trial ends unpaid: the
// trial-ended notice (T13) gets its own key, so dismissing the D-3 one (T3)
// does not hide it.
const pendingKey = (ref, trial) =>
  trial?.state === "ended" ? `cosmo_seen_pending_${ref}_trial_ended` : `cosmo_seen_pending_${ref}`
const migrationKey = (deadline) => `cosmo_seen_migration_${deadline}`

/**
 * Shown once per pending payment (keyed by its ref) and, without a pending
 * payment, once per boleto migration deadline. Dismissable — afterwards only
 * the persistent banner remains.
 */
export default function PendingPaymentModal() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const pending = useSelector(selectPending)
  const migration = useSelector(selectMigration)
  const trial = useSelector(selectTrial)
  const [openKey, setOpenKey] = useState(null)
  const [verifying, setVerifying] = useState(false)

  const hidden = PENDING_UI_HIDDEN_PATHS.includes(location.pathname)
  const key = pending
    ? pendingKey(pending.ref, trial)
    : migration
    ? migrationKey(migration.deadline)
    : null

  useEffect(() => {
    if (!key || hidden || wasSeen(key)) {
      setOpenKey(null)
      return
    }
    setOpenKey(key)
  }, [key, hidden])

  if (!key || openKey !== key) return null

  const dismiss = () => {
    markSeen(key)
    setOpenKey(null)
  }

  const handleVerify = async () => {
    setVerifying(true)
    try {
      await dispatch(fetchSubscriptionStatus()).unwrap()
    } catch {
      // the banner stays; nothing else to do
    } finally {
      setVerifying(false)
    }
  }

  const copy = pending ? pendingNotice(pending, trial) : migrationNotice(migration.deadline)

  return (
    <StyledDialog open onClose={dismiss}>
      <Box sx={{ p: 3, textAlign: "center", color: "#2D2D23" }}>
        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          sx={{ fontWeight: 700, fontFamily: font }}
        >
          {copy.title}
        </Typography>
        <Typography sx={{ mb: 3, fontFamily: font, lineHeight: 1.6 }}>{copy.body}</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {pending ? (
            <>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  // Acting on it counts as seen: the banner stays as the reminder.
                  dismiss()
                  openPendingPayment(pending, navigate)
                }}
                sx={primaryButtonSx}
              >
                {pendingActionLabel(pending.kind)}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleVerify}
                disabled={verifying}
                sx={outlinedButtonSx}
              >
                {verifying ? "Verificando…" : "Já paguei, verificar"}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                dismiss()
                navigate("/subscription")
              }}
              sx={primaryButtonSx}
            >
              Escolher Pix ou cartão
            </Button>
          )}
          <Button variant="text" fullWidth onClick={dismiss} sx={textButtonSx}>
            {pending ? "Continuar mesmo assim" : "Depois"}
          </Button>
        </Box>
      </Box>
    </StyledDialog>
  )
}
